import { UseFormReturn } from 'react-hook-form'
import { WidgetFormValues } from '@/lib/widget-schema'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription,
} from '@/components/ui/card'
import {
	FormField,
	FormItem,
	FormLabel,
	FormControl,
	FormDescription,
	FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { MessageSquare, Bug, Lightbulb, Star, MessageCircle, Mail } from 'lucide-react'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import React from 'react'
import { Switch } from '@/components/ui/switch'
import { PremiumFeature } from '@/components/ui/premium-feature'
import { useFeatureLimit } from '@/hooks/useFeatureLimit'
import { useTranslation } from '@/hooks/useTranslation'

interface Step1Props {
	form: UseFormReturn<WidgetFormValues>
	isPremium?: boolean
}

interface ModuleToggleItemProps {
	icon: React.ComponentType<any>
	title: string
	description?: string
	field: string
	form: any
	disabled?: boolean
	premiumOnly?: boolean
}

function ModuleToggleItem({
	icon: Icon,
	title,
	description,
	field,
	form,
	disabled,
	premiumOnly = false,
}: ModuleToggleItemProps) {
	return (
		<FormField
			control={form.control}
			name={field}
			render={({ field: formField }) => (
				<FormItem className="flex items-center justify-between space-x-4 rounded-lg border p-4">
					<div className="flex flex-1 items-center space-x-4">
						<div className="flex-shrink-0 mt-0.5">
							<Icon className="h-5 w-5 text-muted-foreground" />
						</div>
						<div className="flex-1 space-y-1">
							<FormLabel className={disabled ? 'text-muted-foreground' : ''}>
								{title}
							</FormLabel>
							{description && <FormDescription>{description}</FormDescription>}
						</div>
					</div>
					<FormField
						control={form.control}
						name={field}
						render={({ field }) => (
							<Switch
								checked={field.value}
								onCheckedChange={field.onChange}
								disabled={disabled}
							/>
						)}
					/>
				</FormItem>
			)}
		/>
	)
}

export function Step1_Functionality({ form }: Step1Props) {
	const { t } = useTranslation()
	const liveChatLimit = useFeatureLimit('can_use_live_chat')
	const canUseLiveChat = liveChatLimit.allowed ?? false

	return (
		<Card>
			<CardHeader>
				<CardTitle>{t('widgetBuilder.step1.title')}</CardTitle>
				<CardDescription>
					{t('widgetBuilder.step1.description')}
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-6">
				<FormField
					control={form.control}
					name="name"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('widgetBuilder.step1.widgetName')}</FormLabel>
							<FormDescription>
								{t('widgetBuilder.step1.widgetNameDescription')}
							</FormDescription>
							<FormControl>
								<Input placeholder={t('widgetBuilder.step1.widgetNamePlaceholder')} {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<div className="space-y-3">
					<FormLabel>{t('widgetBuilder.step1.widgetModules')}</FormLabel>
					<FormDescription>
						{t('widgetBuilder.step1.selectFeedbackTypes')}
					</FormDescription>
					<ModuleToggleItem
						icon={MessageSquare}
						title={t('widgetBuilder.step1.collectFeedbackSurveys')}
						description={t('widgetBuilder.step1.collectFeedbackSurveysDescription')}
						field="enable_survey"
						form={form}
					/>
					
					{form.watch('enable_survey') && (
						<div className="ml-4 pl-4 border-l-2 border-muted">
							<FormField
								control={form.control}
								name="primary_feedback_type"
								render={({ field }) => (
									<FormItem>
										<FormLabel>{t('widgetBuilder.step1.primaryFeedbackType')}</FormLabel>
										<FormDescription>
											{t('widgetBuilder.step1.primaryFeedbackTypeDescription')}
										</FormDescription>
										<Select onValueChange={field.onChange} value={field.value}>
											<FormControl>
												<SelectTrigger>
													<SelectValue placeholder={t('widgetBuilder.step1.selectFeedbackType')} />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												<SelectItem value="nps">{t('widgetBuilder.step1.npsSurvey')}</SelectItem>
												<SelectItem value="csat">{t('widgetBuilder.step1.csatSurvey')}</SelectItem>
												<SelectItem value="custom">{t('widgetBuilder.step1.customQuestions')}</SelectItem>
												<SelectItem value="testimonial">
													{t('widgetBuilder.step1.testimonialReviewForm')}
												</SelectItem>
											</SelectContent>
										</Select>
									</FormItem>
								)}
							/>
						</div>
					)}
					
					<ModuleToggleItem
						icon={Star}
						title={t('widgetBuilder.step1.reviews')}
						description={t('widgetBuilder.step1.reviewsDescription')}
						field="enable_reviews"
						form={form}
					/>
					<ModuleToggleItem
						icon={Bug}
						title={t('widgetBuilder.step1.bugReporting')}
						description={t('widgetBuilder.step1.bugReportingDescription')}
						field="enable_bug_reporting"
						form={form}
						disabled={false}
					/>
					<ModuleToggleItem
						icon={Mail}
						title={t('widgetBuilder.step1.contactMessages', 'Contact Messages')}
						description={t('widgetBuilder.step1.contactMessagesDescription', 'Receive general inquiries, questions, and messages from your users.')}
						field="enable_contact_messages"
						form={form}
						disabled={false}
					/>
					<ModuleToggleItem
						icon={Lightbulb}
						title={t('widgetBuilder.step1.featureSuggestions')}
						description={t('widgetBuilder.step1.featureSuggestionsDescription')}
						field="enable_feature_requests"
						form={form}
						disabled={false}
					/>
					<PremiumFeature
						isLocked={!canUseLiveChat}
						message={t('widgetBuilder.step1.liveChatRequiresPro')}>
						<ModuleToggleItem
							icon={MessageCircle}
							title={t('widgetBuilder.step1.liveChat')}
							description={t('widgetBuilder.step1.liveChatDescription')}
							field="enable_live_chat"
							form={form}
							disabled={!canUseLiveChat}
							premiumOnly={true}
						/>
					</PremiumFeature>
				</div>
			</CardContent>
		</Card>
	)
}
